// ────────────────────────────────────────────────────────────────────
// Import wizard: map columns → dry-run preview → commit.
//
// The mapping is pre-filled from lib/importClassify, the same keyword sets the
// console used to decide this sheet was a thickness survey — so the decision
// and the pre-fill cannot disagree.
// ────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { X, CheckCircle2 } from "lucide-react";
import { apiClient, getCachedUser, getActiveTenant, STAFF_ROLES } from "@/lib/api";
import { seedThicknessMapping } from "@/lib/importClassify";
import { Stat, PickerNote } from "@/components/intake/primitives";
import { RejectsBlock, conflictsFrom409 } from "@/screens/intake/importShared";
import type { IntakeFile, IntakeImportMapping, IntakeImportPreview } from "@/lib/types";

export function ImportModal({ file, onClose }: { file: IntakeFile; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const inspectQ = useQuery({ queryKey: ["intake-inspect", file.id], queryFn: () => apiClient.intakeInspect(file.id), staleTime: 60_000 });
  // Saved mapping templates. The endpoint needs a tenant: customers are
  // tenant-locked server-side, but staff only send X-Tenant-Id when a tenant
  // is active (api.ts interceptor) — without one the call is a guaranteed 400,
  // so don't fire it (and never retry-spam on failure).
  const isStaffUser = (STAFF_ROLES as readonly string[]).includes(getCachedUser()?.role ?? "");
  const templatesQ = useQuery({
    queryKey: ["intake-templates"],
    queryFn: apiClient.intakeTemplates,
    staleTime: 60_000,
    retry: false,
    enabled: !isStaffUser || Boolean(getActiveTenant()),
  });
  const cols = inspectQ.data?.columns ?? [];
  const [map, setMap] = useState<IntakeImportMapping>({ cml_no: "", thickness: "", measured_at: "", thickness_unit: "mm" });
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!seeded && cols.length) {
      setMap((m) => ({ ...m, ...seedThicknessMapping(cols) }));
      setSeeded(true);
    }
  }, [cols, seeded]);

  const preview = useMutation({ mutationFn: (m: IntakeImportMapping) => apiClient.intakeImportPreview(file.id, m) });
  const commit = useMutation({ mutationFn: () => apiClient.intakeImportCommit(file.id, map),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["intake"] });
      if (res.template_saved) void qc.invalidateQueries({ queryKey: ["intake-templates"] });
    } });
  const ready = map.cml_no && map.thickness && map.measured_at;
  const r: IntakeImportPreview | undefined = preview.data;
  const done = commit.data;
  // DB conflicts gate the commit: superseding measured history is a decision.
  // A commit 409 carries a recomputed list (readings can change between preview
  // and commit) — when present it supersedes the preview's.
  const conflicts409 = commit.isError ? conflictsFrom409(commit.error) : null;
  const dbConflicts = conflicts409 ?? r?.db_conflicts ?? [];
  const needsConfirm = dbConflicts.length > 0;

  const Field = ({ label, k, optional }: { label: string; k: keyof IntakeImportMapping; optional?: boolean }) => (
    <label className="flex items-center gap-2">
      <span className="font-mono uppercase w-24 shrink-0" style={{ fontSize: 9, letterSpacing: ".1em", color: optional ? "var(--ink-4)" : "var(--ink-2)" }}>{label}</span>
      <select value={(map[k] as string) ?? ""} onChange={(e) => { setMap((m) => ({ ...m, [k]: e.target.value || undefined })); preview.reset(); }}
              className="flex-1 font-mono px-2 py-1 outline-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
        <option value="">{optional ? "— none —" : "— pick column —"}</option>
        {cols.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </label>
  );

  return (
    <div className="fixed inset-0 z-[1200] flex items-start justify-center pt-[9vh]" style={{ background: "rgba(10,15,24,0.5)" }} onClick={onClose}>
      <div className="w-[560px] max-w-[94vw] shadow-2xl max-h-[82vh] overflow-auto" onClick={(e) => e.stopPropagation()}
           style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
        <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0" style={{ borderColor: "var(--hairline)", background: "var(--paper)" }}>
          <div className="font-mono uppercase truncate" style={{ fontSize: 10, letterSpacing: ".16em", color: "var(--ink-3)" }}>
            / IMPORT TO {file.asset_tag} · <span style={{ color: "var(--ink-1)" }}>{file.filename}</span>
          </div>
          <button onClick={onClose}><X size={14} style={{ color: "var(--ink-3)" }} /></button>
        </div>

        <div className="p-4">
          {inspectQ.isPending && <PickerNote>Reading file…</PickerNote>}
          {inspectQ.isError && <PickerNote>Could not read this file.</PickerNote>}
          {cols.length > 0 && (
            <>
              <div className="font-mono uppercase mb-2" style={{ fontSize: 9, letterSpacing: ".18em", color: "var(--ink-4)" }}>Map columns</div>
              <div className="flex flex-col gap-1.5">
                {Boolean(templatesQ.data?.length) && (
                  <label className="flex items-center gap-2">
                    <span className="font-mono uppercase w-24 shrink-0" style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--ink-2)" }}>Template</span>
                    <select value="" onChange={(e) => {
                        const t = templatesQ.data!.find((x) => String(x.id) === e.target.value);
                        if (!t) return;
                        // Blank out template columns absent from THIS file — otherwise the
                        // Field select renders empty while the map still holds the stale
                        // column (hidden-but-set → confusing server error on preview).
                        const cfg: Partial<IntakeImportMapping> = { ...t.config };
                        for (const k of ["cml_no", "thickness", "measured_at", "t_nominal", "t_min"] as const) {
                          const v = cfg[k];
                          if (typeof v === "string" && v && !cols.includes(v)) {
                            if (k === "cml_no" || k === "thickness" || k === "measured_at") cfg[k] = "";
                            else cfg[k] = undefined;
                          }
                        }
                        setMap((m) => ({ ...m, ...cfg, save_template: m.save_template }));
                        preview.reset();
                      }}
                      className="flex-1 font-mono px-2 py-1 outline-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                      <option value="">— use a saved template —</option>
                      {templatesQ.data!.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                )}
                <Field label="CML no" k="cml_no" />
                <Field label="Thickness" k="thickness" />
                <Field label="Date" k="measured_at" />
                <Field label="Nominal" k="t_nominal" optional />
                <Field label="T-min" k="t_min" optional />
                <label className="flex items-center gap-2">
                  <span className="font-mono uppercase w-24 shrink-0" style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--ink-4)" }}>Unit</span>
                  <select value={map.thickness_unit} onChange={(e) => { setMap((m) => ({ ...m, thickness_unit: e.target.value as "mm" | "inch" })); preview.reset(); }}
                          className="font-mono px-2 py-1 outline-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                    <option value="mm">mm</option><option value="inch">inch</option>
                  </select>
                </label>
              </div>

              {/* re-previewing revokes a prior supersede confirmation AND any stale 409
                  conflict state — the tick must always consent to the list on screen.
                  The cleared map is passed explicitly: the request matches the state. */}
              <button onClick={() => {
                        const cleared = { ...map, overwrite_conflicts: undefined };
                        setMap(cleared);
                        commit.reset();
                        preview.mutate(cleared);
                      }}
                      disabled={!ready || preview.isPending}
                      className="mt-3 font-mono uppercase w-full py-2 border transition hover:bg-[rgba(var(--accent-rgb)_/_0.08)] active:translate-y-px disabled:opacity-40"
                      style={{ fontSize: 10, letterSpacing: ".12em", color: "var(--accent-deep)", borderColor: "var(--accent)", fontWeight: 700 }}>
                {preview.isPending ? "Checking…" : "Preview import"}
              </button>

              {r && (
                <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--hairline)" }}>
                  <div className="grid grid-cols-3 mb-3" style={{ border: "1px solid var(--hairline)" }}>
                    <Stat label="CMLs" value={r.cmls} />
                    <Stat label="Readings" value={r.surveys} />
                    <Stat label="Min mm" value={r.min_mm ?? 0} />
                  </div>
                  {(r.data_quality.dropped_no_key + r.data_quality.dropped_no_date + r.data_quality.dropped_reading) > 0 && (
                    <div className="font-mono mb-2 px-3 py-2" style={{ fontSize: 10.5, color: "var(--warn)", background: "rgba(var(--accent-rgb) / .09)", border: "1px solid rgba(var(--accent-rgb) / .4)" }}>
                      ▲ Dropped by data-integrity guards: {r.data_quality.dropped_no_key} no-CML · {r.data_quality.dropped_no_date} bad-date · {r.data_quality.dropped_reading} bad-reading
                      {r.data_quality.reasons.length > 0 && (
                        <div className="mt-1" style={{ color: "var(--ink-3)" }}>{r.data_quality.reasons.slice(0, 3).join(" · ")}{r.data_quality.reasons.length > 3 ? " …" : ""}</div>
                      )}
                    </div>
                  )}
                  {Boolean(r.data_quality.rejects?.length) && (
                    <RejectsBlock rejects={r.data_quality.rejects!} filename={file.filename} />
                  )}
                  {Boolean(r.data_quality.in_file_conflicts?.length) && (
                    /* asset_tag omitted on purpose: everything in this modal imports into the one filed asset */
                    <div className="font-mono mb-2 px-3 py-2" style={{ fontSize: 10.5, color: "var(--warn)", background: "rgba(var(--accent-rgb) / .09)", border: "1px solid rgba(var(--accent-rgb) / .4)" }}>
                      ▲ {r.data_quality.in_file_conflicts!.length} in-file conflicts — same CML+date with differing values; the later-timestamped reading wins:{" "}
                      {r.data_quality.in_file_conflicts!.slice(0, 3).map((c) => `${c.cml_no}/${c.date}: ${c.first_mm} → ${c.second_mm}`).join(" · ")}
                      {r.data_quality.in_file_conflicts!.length > 3 ? " …" : ""}
                    </div>
                  )}
                  {r.sample.length > 0 && (
                    <table className="w-full font-mono" style={{ fontSize: 11, borderCollapse: "collapse" }}>
                      <thead><tr style={{ color: "var(--ink-4)" }}><th className="text-left py-1">CML</th><th className="text-left py-1">Date</th><th className="text-right py-1">mm</th></tr></thead>
                      <tbody>{r.sample.map((s, i) => (
                        <tr key={i} style={{ borderTop: "1px solid var(--hairline)" }}>
                          <td className="py-1" style={{ color: "var(--ink-1)" }}>{s.cml_no}</td>
                          <td className="py-1 tabular-nums" style={{ color: "var(--ink-3)" }}>{s.date}</td>
                          <td className="py-1 text-right tabular-nums" style={{ color: "var(--ink-2)" }}>{s.mm.toFixed(2)}</td>
                        </tr>))}</tbody>
                    </table>
                  )}
                  {done ? (
                    <div className="mt-3 font-mono px-3 py-2.5 flex items-center justify-between gap-3"
                         style={{ fontSize: 11, color: "var(--ok)", background: "rgba(21,128,61,.07)", border: "1px solid rgba(21,128,61,.4)" }}>
                      <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} /> Imported {done.cmls} CMLs · {done.surveys} readings into {done.asset_tag}</span>
                      <button onClick={() => navigate(`/assets/${file.asset_id}`)} className="uppercase whitespace-nowrap px-2 py-0.5 border hover:bg-[rgba(21,128,61,0.1)]"
                              style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--ok)", borderColor: "rgba(21,128,61,0.5)", fontWeight: 700 }}>Open asset →</button>
                    </div>
                  ) : (
                    <>
                      <div className="mt-3 font-mono px-3 py-2" style={{ fontSize: 10, color: "var(--ink-4)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                        Reviewed — these readings import into <b>{r.asset_tag}</b> as CMLs + measurements. The importer is idempotent (re-running upserts, never duplicates) · หลักการ "ไม่เดา"
                      </div>
                      {needsConfirm && (
                        <div className="mt-3 px-3 py-2" style={{ border: "1px solid var(--err)", background: "rgba(192,57,43,.04)" }}>
                          <div className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--err)", fontWeight: 700 }}>
                            {dbConflicts.length} existing readings differ
                          </div>
                          <table className="w-full font-mono mt-1" style={{ fontSize: 10 }}>
                            <tbody>
                              {dbConflicts.slice(0, 8).map((c) => (
                                <tr key={`${c.cml_no}-${c.date}`}><td>{c.cml_no}</td><td>{c.date}</td>
                                  <td className="tabular-nums" align="right">{c.existing_mm} → {c.new_mm} mm</td></tr>
                              ))}
                            </tbody>
                          </table>
                          {dbConflicts.length > 8 && (
                            <div className="font-mono" style={{ fontSize: 9, color: "var(--ink-3)" }}>+{dbConflicts.length - 8} more</div>
                          )}
                          <label className="flex items-center gap-2 mt-1.5 font-mono" style={{ fontSize: 10, color: "var(--ink-2)" }}>
                            <input type="checkbox" checked={!!map.overwrite_conflicts}
                                   onChange={(e) => setMap((m) => ({ ...m, overwrite_conflicts: e.target.checked || undefined }))} />
                            Supersede {dbConflicts.length} existing readings — the old values stay in history; these become the latest
                          </label>
                        </div>
                      )}
                      {r.cmls > 0 && (
                        <>
                          <input value={map.save_template ?? ""} placeholder="Save mapping as template (optional)"
                                 aria-label="Save mapping as template"
                                 onChange={(e) => setMap((m) => ({ ...m, save_template: e.target.value || undefined }))}
                                 className="mt-3 w-full font-mono px-2 py-1 outline-none"
                                 style={{ fontSize: 11, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }} />
                          <button onClick={() => commit.mutate()} disabled={commit.isPending || (needsConfirm && !map.overwrite_conflicts)}
                                  className="mt-2 font-mono uppercase w-full py-2 border transition hover:brightness-95 active:translate-y-px disabled:opacity-40"
                                  style={{ fontSize: 10, letterSpacing: ".12em", color: "#fff", background: "var(--ok)", borderColor: "var(--ok)", fontWeight: 700 }}>
                            {commit.isPending ? "Importing…"
                              : `${needsConfirm ? "Supersede & import" : "Confirm import"} — ${r.cmls} CMLs · ${r.surveys} readings`}
                          </button>
                        </>
                      )}
                      {commit.isError && (conflicts409
                        ? <div className="mt-2 font-mono" style={{ fontSize: 11, color: "var(--err)" }}>Import blocked — existing readings differ (list above). Nothing was written; confirm supersede to retry.</div>
                        : <div className="mt-2 font-mono" style={{ fontSize: 11, color: "var(--err)" }}>Import failed — nothing was written. Check the mapping.</div>)}
                    </>
                  )}
                </div>
              )}
              {preview.isError && <div className="mt-2 font-mono" style={{ fontSize: 11, color: "var(--err)" }}>Could not parse with this mapping — check the columns.</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

